import type { ReactNode } from "react";

import { Breadcrumbs, type BreadcrumbItem } from "~/components/breadcrumbs";
import { FlickeringGrid } from "~/components/ui/flickering-grid";
import { cn } from "~/lib/utils";

interface PublicPageHeaderProps {
  className?: string;
  breadcrumbItems?: BreadcrumbItem[];
  beforeTitle?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  subtitleClassName?: string;
  subtitleAction?: ReactNode;
  afterTitle?: ReactNode;
  children?: ReactNode;
  density?: "compact" | "hero";
  showFlickeringGrid?: boolean;
}

export function PublicPageHeader({
  className,
  breadcrumbItems,
  beforeTitle,
  title,
  subtitle,
  subtitleClassName,
  subtitleAction,
  afterTitle,
  children,
  density = "compact",
  showFlickeringGrid = true,
}: PublicPageHeaderProps) {
  const isHero = density === "hero";

  return (
    <header
      className={cn(
        "relative z-10 flex flex-col border-b border-border bg-background pb-4 pt-20 md:pb-6 md:pt-20",
        isHero
          ? "min-h-[200px] justify-center gap-4 md:min-h-[250px] md:gap-6"
          : "gap-4 md:gap-6",
        className,
      )}
    >
      {showFlickeringGrid && (
        <div className="absolute left-0 top-0 -z-10 h-[200px] w-full mask-[linear-gradient(to_top,transparent_25%,black_95%)]">
          <FlickeringGrid
            className="absolute left-0 top-0 size-full"
            squareSize={4}
            gridGap={6}
            // Warm neutral matching --muted-foreground; canvas needs a
            // concrete colour, so this cannot read the token directly.
            color="#625C50"
            maxOpacity={0.2}
            flickerChance={0.05}
          />
        </div>
      )}
      <div className="max-w-8xl container mx-auto flex w-full flex-col gap-4 px-4 md:gap-6 md:px-8">
        {breadcrumbItems && <Breadcrumbs items={breadcrumbItems} />}
        {beforeTitle}
        {title && <h1 className="h1-content text-balance">{title}</h1>}
        {subtitle && (
          <div
            className={cn(
              subtitleAction &&
                "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
            )}
          >
            <p
              className={cn(
                "max-w-3xl text-base leading-relaxed text-muted-foreground lg:text-lg",
                subtitleClassName,
              )}
            >
              {subtitle}
            </p>
            {subtitleAction}
          </div>
        )}
        {afterTitle}
        {children}
      </div>
    </header>
  );
}
